import { useMutation } from '@tanstack/react-query';
import { createSpotifyPlaylist } from '../api/spotify/spotify-playlist';
import { createYoutubePlaylist } from '../api/youtube/youtube-playlist';
import { logger } from '../shared/logging/logger';
import type { PlaylistReturnObject } from '../interfaces/playlist-return-object';

// Playlist-export mutations (Playlist.tsx). Wrap the UNCHANGED api functions
// (which own OAuth refresh, track search, concurrency, and the {error}-return
// convention). The call site keeps its try/catch + result.error handling via
// mutateAsync. The api fns catch internally and RESOLVE with { error } instead
// of throwing, so the global MutationCache would log that as a success — the
// per-hook onSuccess below logs the logical error so failed exports are visible
// in the diagnostic 'query' log too.

type SpotifyExportInput = { songs: { artist: string; title: string }[]; name: string; playlistId?: string };
type YoutubeExportInput = { youtubeIds: string[]; name: string; playlistId?: string };

export function useExportSpotifyPlaylist() {
  return useMutation({
    mutationKey: ['playlist', 'export', 'spotify'],
    mutationFn: ({ songs, name, playlistId }: SpotifyExportInput): Promise<PlaylistReturnObject> =>
      createSpotifyPlaylist(songs, name, playlistId),
    onSuccess: (data) => {
      if (data?.error) logger.warn('query', 'spotify export returned error', { error: data.error });
    },
  });
}

export function useExportYoutubePlaylist() {
  return useMutation({
    mutationKey: ['playlist', 'export', 'youtube'],
    mutationFn: ({ youtubeIds, name, playlistId }: YoutubeExportInput): Promise<PlaylistReturnObject> =>
      createYoutubePlaylist(youtubeIds, name, playlistId),
    onSuccess: (data) => {
      if (data?.error) logger.warn('query', 'youtube export returned error', { error: data.error });
    },
  });
}
